/**
 * AnchorLinks Component
 *
 * Custom component for displaying anchor links.
 * Shows as a carousel on mobile and a horizontal row on desktop.
 * Used in ShowroomDetails for the relatedPages field.
 */

import React, {useMemo, useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';

import {
  Box,
  Circle,
  HStack,
  Link as ChakraLink,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Swiper} from '@amblique/base/app/components/shared/swiper';
import {useIsMobile} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';
import {ArrowDownIcon, ArrowRightIcon} from '@amblique/king-living/app/components/icons';

/**
 * Smooth scroll to element with offset for header
 */
const scrollToElement = (elementId, offset = 80) => {
  const element = document.getElementById(elementId);

  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

/**
 * Single anchor link item component
 */
const AnchorLinkItem = ({link, isMobile}) => {
  const isExternal = link.url?.startsWith('http') || link.openInNewTab;
  const isHashLink = link.url?.startsWith('#');
  const key = link.sys?.id || link.url || link.text;

  const linkStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'brandColors.greyscale90',
    fontSize: isMobile ? '14px' : '18px',
    fontWeight: '500',
    lineHeight: '120%',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    _hover: {textDecoration: 'underline'},
  };

  const content = (
    <>
      {link.text}
      <ArrowDownIcon boxSize={isMobile ? '14px' : '16px'} />
    </>
  );

  // Handle hash links with smooth scrolling
  const handleHashClick = (e) => {
    e.preventDefault();

    const elementId = link.url.substring(1); // Remove the # prefix

    scrollToElement(elementId);

    // Update URL hash without jumping
    if (typeof window !== 'undefined') {
      window.history.pushState(null, '', link.url);
    }
  };

  if (isHashLink) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        {...linkStyles}
        onClick={handleHashClick}
      >
        {content}
      </ChakraLink>
    );
  }

  if (isExternal) {
    return (
      <ChakraLink
        key={key}
        href={link.url}
        isExternal={link.openInNewTab}
        {...linkStyles}
      >
        {content}
      </ChakraLink>
    );
  }

  return (
    <ChakraLink
      key={key}
      as={Link}
      to={link.url}
      {...linkStyles}
    >
      {content}
    </ChakraLink>
  );
};

AnchorLinkItem.propTypes = {
  link: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    openInNewTab: PropTypes.bool,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  isMobile: PropTypes.bool,
};

export const AnchorLinks = ({links = [], containerProps = {}, ...props}) => {
  const isMobile = useIsMobile();
  const swiperRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const swiperOptions = useMemo(() => ({
    navigation: false,
    pagination: false,
    scrollbar: false,
    autoHeight: false,
    spaceBetween: 16,
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    freeMode: true,
    onSwiper: (swiper) => {
      swiperRef.current = swiper;
    },
    onSlideChange: (swiper) => {
      setCanScrollRight(!swiper.isEnd);
    },
    onReachEnd: () => {
      setCanScrollRight(false);
    },
    onFromEdge: () => {
      setCanScrollRight(true);
    },
  }), []);

  // Check if we can scroll on mount
  useEffect(() => {
    if (swiperRef.current) {
      setCanScrollRight(!swiperRef.current.isEnd);
    }
  }, [links]);

  const handleScrollRight = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  if (!links || links.length === 0) {
    return null;
  }

  // Mobile: Show as carousel with scroll indicator
  if (isMobile) {
    return (
      <Box
        className="anchor-links-container"
        mb={6}
        position="relative"
        {...containerProps}
      >
        <Box
          px={4}
          sx={{
            '.swiper': {
              overflow: 'visible',
            },
            '.swiper-slide': {
              width: 'auto !important',
            },
          }}
        >
          <Swiper options={swiperOptions} presets="">
            {links.map((link) => (
              <Box key={link.sys?.id || link.url || link.text} pr={0}>
                <AnchorLinkItem isMobile={true} link={link} />
              </Box>
            ))}
          </Swiper>
        </Box>

        {/* Right scroll indicator */}
        {canScrollRight && (
          <Circle
            as="button"
            bg="white"
            border="1px solid"
            borderColor="gray.300"
            cursor="pointer"
            position="absolute"
            right={2}
            shadow="sm"
            size="32px"
            top="50%"
            transform="translateY(-50%)"
            zIndex={10}
            _hover={{
              bg: 'gray.50',
              shadow: 'md',
            }}
            onClick={handleScrollRight}
          >
            <ArrowRightIcon boxSize="16px" color="gray.600" />
          </Circle>
        )}
      </Box>
    );
  }

  // Desktop: Show as horizontal row
  return (
    <Box className="anchor-links-container" mb={6} px={{base: 4, lg: 0}} {...containerProps}>
      <HStack
        flexWrap="wrap"
        gap="32px"
        justify="flex-start"
        spacing={0}
        {...props}
      >
        {links.map((link) => (
          <AnchorLinkItem key={link.sys?.id || link.url || link.text} isMobile={false} link={link} />
        ))}
      </HStack>
    </Box>
  );
};

AnchorLinks.displayName = 'AnchorLinks';

AnchorLinks.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      openInNewTab: PropTypes.bool,
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
    })
  ),
  containerProps: PropTypes.object,
};

export default AnchorLinks;
